// import * as Sentry from '@sentry/browser';
import camelcaseKeys from 'camelcase-keys';
import * as Sentry from '@sentry/browser';
import * as lib from '~/dependencies';
import { getToken } from '~/helpers';
import { UPDATER_CONFIG } from '~/config/config';

export function getEnvFromWindowHref() {
  if (window.location.href.includes('d2046zq7ft8yun')) return 'PROD';
  if (window.location.href.includes('d1gaikb7mx9zak')) return 'DEV';
  if (window.location.href.includes('djhk1kho598u6')) return 'RC';
  if (window.location.href.includes('localhost')) return 'LOCALHOST';
  return 'PROD';
}

const API_VERSION = 'v1';

// URL BASE
const url = lib.axios.create({
  baseURL: 'https://api.nuacom.ie',
});

const axiosBaseUrlNew = lib.axios.create({
  baseURL: 'https://api.api-nuacom.com',
  timeout: 30000,
});

const updateCheckerUrl = lib.axios.create({
  baseURL: UPDATER_CONFIG[getEnvFromWindowHref()].UPDATE_CHECKER_URL,
  headers: {
    'Cache-Control': 'no-cache',
  },
});

// API used to get the queue status
export const axiosBaseUrlWss = lib.axios.create({
  baseURL: 'https://wss-queues.nuacom.net',
  timeout: 30000,
});

axiosBaseUrlWss.interceptors.request.use(config => {
  const configuration = config;

  configuration.headers = {
    'Client-Security-Token': getToken(),
    'Content-Type': 'application/json',
  };

  return config;
});

// Refer to dynamic integration URLs
const url4 = lib.axios.create();

axiosBaseUrlNew.interceptors.request.use(config => {
  const token = getToken();
  const configuration = config;

  configuration.headers = {
    'Client-Security-Token': token,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  return configuration;
});

// Refer to dynamic URLs for integrations
url4.interceptors.request.use(config => {
  const token = getToken();
  const configuration = config;

  configuration.headers = {
    'Client-Security-Token': token,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  return configuration;
});

url.interceptors.request.use(config => {
  const token = getToken();
  const configuration = config;
  const isLogin = /\/login|\/credential|\/sso|\/forgot_login/.test(
    configuration?.url
  );

  configuration.headers = {
    'Client-Security-Token': token,
    'Content-Type': 'application/json',
  };

  if (!token && !isLogin) {
    Sentry.captureMessage('nuacom_log: token is missing', Sentry.Severity.Info);
  }

  return configuration;
});

function createSentry401ReportRedirectToSignIn() {
  Sentry.captureMessage(
    'NUACOM_LOG: Destkop Softphone logged out as result of unauthorized access. Status Code 401.',
    Sentry.Severity.Info
  );

  window.location = '/logout';
}

// NOTE: List of urls which response data will be automatically camelcased
const autoCamelCaseUrlList = [
  '/new/v1/account-queue-headset',
  '/queues/membership',
  '/new/v1/integrations/gohighlevel',
  '/new/v1/auth/login',
  '/new/sso/google/token?code=',
  '/new/sso/microsoft/token?code=',
  '/new/v1/sms-templates',
  '/v1/departments',
  'v1/conversations/unread-count',
  '/new/v1/users_list',
  '/new/v1/sms/sender_names',
  '/new/v1/conversations',
  '/mapped-numbers',
];

url.interceptors.response.use(
  response => {
    if (response.status === 401) {
      createSentry401ReportRedirectToSignIn();
    }

    if (
      response.status === 200 &&
      autoCamelCaseUrlList.some(urlItem =>
        response.config.url.includes(urlItem)
      )
    ) {
      response.data = camelcaseKeys(response.data, { deep: true });
    }

    return response;
  },
  error => {
    const isLogin = /\/login|\/credential|\/sso|\/forgot_login/.test(
      error?.response?.config.url
    );

    if (error?.response?.status === 401 && !isLogin) {
      createSentry401ReportRedirectToSignIn();
    }

    return Promise.reject(error);
  }
);

axiosBaseUrlNew.interceptors.response.use(response => {
  if (
    response.data &&
    autoCamelCaseUrlList.some(urlItem => response.config.url.includes(urlItem))
  ) {
    response.data = camelcaseKeys(response.data, { deep: true });
  }

  return response;
});

const extraHeaders = {
  headers: {
    'Content-Type': 'application/json',
  },
};

const api = {
  auth: {
    get: {
      authSSOSecureCode: (provider, code) =>
        url.get(`/new/sso/${provider}/token?code=${code}`),
    },
    post: {
      revokeToken: () => axiosBaseUrlNew.post(`/new/${API_VERSION}/logout`),
    },
  },
  contacts: {
    get: {
      list: (type = 'All', page) =>
        url.get(
          `/${API_VERSION}/contacts${
            type === 'Personal' ? '?personal=true' : ''
          }${
            type === 'Personal' ? `&page=${page}` : `?page=${page}`
          }&sort=contact_name&requested_time=1`
        ),
      checkUpdates: (date, personal) =>
        url.get(
          `/${API_VERSION}/contacts?last_updated=${date}&deleted=true&personal=${personal}&sort=contact_name&requested_time=1`
        ),
      contactInfo: id => url.get(`/${API_VERSION}/contacts/${id}`),
      byNumber: number => url.get(`/${API_VERSION}/contacts?q=${number}`),
    },
    post: {
      newContact: data => url.post(`/${API_VERSION}/contacts`, data),
    },
    patch: {
      updateContact: (id, data) =>
        url.patch(`/${API_VERSION}/contacts/${id}`, data),
    },
    delete: {
      deleteContact: id => url.delete(`/${API_VERSION}/contacts/${id}`),
    },
    groups: {
      list: () => url.get(`/${API_VERSION}/groups`),
      create: ({ name }) =>
        url.post(`/${API_VERSION}/groups`, { group_name: name }),
    },
    organization: {
      list: () => url.get(`/${API_VERSION}/organizations`),
      create: ({ name }) => url.post(`/${API_VERSION}/organizations`, { name }),
    },
  },
  callNotes: {
    get: {
      noteInfo: (callId, sipCallId) =>
        axiosBaseUrlNew.get(
          `/new/${API_VERSION}/call-notes?callIDs[]=${callId}${
            sipCallId ? `&callIDs[]=${sipCallId}` : ''
          }`
        ),
    },
    post: {
      registerNote: data => url.post(`/${API_VERSION}/call_note`, data),
    },
  },
  calls: {
    get: {
      logsWithFilterOptions: customFiltersQuery =>
        url.get(`/new/${API_VERSION}/call-log?${customFiltersQuery}`),
      logsWithDate: (date, extension) =>
        url.get(
          `/${API_VERSION}/call_logs?from_date=${date}&extensions=${extension}`
        ),
      logDetail: id => url.get(`/${API_VERSION}/call_logs/${id}`),
      record: (callId, format) =>
        url.get(
          `/${API_VERSION}/recording?call_id=${callId}&format=${format}`,
          {
            responseType: 'blob',
          }
        ),
      logsByPhoneNumber: (number, limit, fromDate, toDate, config) =>
        url.get(
          `/new/${API_VERSION}/call-log?per_page=${limit}&number=${number}&from_date=${fromDate}&to_date=${toDate}&sort_by=call_date&sort_dir=desc`,
          config
        ),
    },
    delete: {
      record: callId =>
        axiosBaseUrlNew.delete(`/new/${API_VERSION}/call-recording/${callId}`),
    },
  },
  accounts: {
    get: {
      accountInfo: () => url.get(`/${API_VERSION}/account_info`),
      extensionInfo: () => url.get(`/${API_VERSION}/extension_info`),
      allExtensions: () => url.get(`/${API_VERSION}/extensions`),
      allowedExtensions: () =>
        axiosBaseUrlNew.get(`/new/${API_VERSION}/extensions`),
      outboundNumbers: () =>
        axiosBaseUrlNew.get(
          `/new/${API_VERSION}/extensions/user-info-and-available-cli`
        ),
      departments: () => axiosBaseUrlNew.get(`/new/${API_VERSION}/departments`),
      getSmsSenderNames: () => axiosBaseUrlNew.get(`/new/v1/sms/sender_names`),
      getUserList: () =>
        axiosBaseUrlNew.get(`/new/v1/users_list?includeStaff=1`),
      getTemplates: (page, perPage) =>
        axiosBaseUrlNew.get(
          `/new/v1/sms-templates?sort=name&per_page=${perPage}&page=${page}`
        ),
    },
    post: {
      outboundNumberChange: outboundNumber =>
        url.post(`/${API_VERSION}/user_info_cli`, { number: outboundNumber }),
      setExtension: data =>
        url.post(`/${API_VERSION}/user_info_extension`, data),
    },
  },
  tags: {
    get: {
      allTags: () => url.get(`/${API_VERSION}/call_tag_info`),
      getTag: callId => url.get(`/${API_VERSION}/call_tag/${callId}`),
    },
    post: {
      createTag: data => url.post(`/${API_VERSION}/call_tag_info`, data),
      addCallTag: data => url.post(`/${API_VERSION}/call_tag`, data),
    },
    patch: {
      updateTag: data => url.patch(`/${API_VERSION}/call_tag_info`, data),
    },
    delete: {
      tag: data =>
        url.delete(`/${API_VERSION}/call_tag_info`, { extraHeaders, data }),
      callTag: data =>
        url.delete(`/${API_VERSION}/call_tag`, { extraHeaders, data }),
    },
    fetchCallTags: (callId, callIdTwo, config) =>
      axiosBaseUrlNew.get(
        `/new/v1/call-tags?callIDs[]=${callId}&callIDs[]=${callIdTwo}`,
        config
      ),
    fetchTagsDetails: (availableFor, config) =>
      axiosBaseUrlNew.get(
        `/new/v2/call-tags?page=1&perPage=100&filter[available_for]=${availableFor}`,
        config
      ),
  },
  queues: {
    get: {
      allQueues: () => axiosBaseUrlNew.get(`/new/${API_VERSION}/queues`),
      dynamicQueues: () => url.get(`/new/${API_VERSION}/account-queue-headset`),
      dynamicQueueStatus: (accountId, extension) =>
        axiosBaseUrlWss.get(`/queues/membership/${accountId}/${extension}`),
    },
    post: {
      setAgentStatus: data =>
        url.post(
          `/new/${API_VERSION}/extensions/set-dynamic-agent-queue-status`,
          data
        ),
    },
  },
  users: {
    get: {
      userInfo: () => url.get(`/${API_VERSION}/user_info`),
      getCredentials: token => url.get(`/credential/${token}`),
      favoriteAgents: () => url.get(`/${API_VERSION}/user_favourite_agents`),
    },
    post: {
      login: (email, pass) =>
        url.post(`/new/${API_VERSION}/auth/login`, {
          email,
          pass,
          remember: true,
        }),
      authSecureCode: (email, password, secureCode) =>
        url.post(`/new/${API_VERSION}/auth/login`, {
          email,
          pass: password,
          two_fa_code: secureCode,
          remember: true,
        }),
      changeAccount: account =>
        url.post(`/${API_VERSION}/user_info_change_account`, {
          user_account: account,
        }),
      recoverPassword: email => url.get(`/forgot_login?email=${email}`),
      changePassword: (email, password, newPassword, newPasswordConfirmation) =>
        url.post(`/new/${API_VERSION}/auth/update-password`, {
          email,
          password,
          new_password: newPassword,
          new_password_confirmation: newPasswordConfirmation,
        }),
      updateFavoriteAgents: data =>
        url.post(`/${API_VERSION}/user_favourite_agents`, data),
    },
  },
  feedback: {
    post: {
      sendFeedback: data => url.post(`/new/${API_VERSION}/feedback`, data),
    },
  },
  system: {
    checkUpdates: () => updateCheckerUrl.get(),
  },
  sms: {
    post: {
      /**
       * @deprecated This method will be removed in v1.26.0 as conversations feature deprecated SendSMS component.
       */
      sendSMS: (data, config) => url.post(`/${API_VERSION}/sms`, data, config),
      changeSmsBillType: (data, config) =>
        url.post(`/${API_VERSION}/sms_change_billing_to `, data, config),
    },
    get: {
      smsBillPayRate: () => url.get(`/${API_VERSION}/sms_billpay_rate`),
      /**
       * @deprecated This method will be removed in v1.26.0 as conversations feature deprecated SendSMS component.
       */
      smsSenderNames: () => url.get(`/${API_VERSION}/sms_sender_names`),
    },
  },
  announcements: {
    get: {
      all: () => url.get(`/new/${API_VERSION}/softphone-announcements`),
      byId: id =>
        url.get(`/new/${API_VERSION}/softphone-announcements/${id}/audio`, {
          responseType: 'blob',
        }),
    },
  },
  integrations: {
    get: {
      accountIntegrations: () =>
        url.get(`/new/${API_VERSION}/integrations/list-active-integrations`),
      contactInfoByNumber: number =>
        url.get(`/new/${API_VERSION}/integrations/contacts?phone=${number}`),
      contactOnwerByExternalId: externalId =>
        url.get(
          `/${API_VERSION}/integrations/contact_owner?external_id=${externalId}`
        ),
      getTickets: (endpoint, externalId, callId, config) =>
        url.get(
          `/new/${API_VERSION}/integrations/${endpoint}?external_id=${externalId}&call_id=${callId}`,
          { ...config }
        ),
      getTicketFields: config =>
        url.get(`/new/${API_VERSION}/integrations/auto_task/ticket-fields`, {
          ...config,
        }),
      getAutoTaskCompanies: config =>
        url.get(`/new/${API_VERSION}/integrations/auto_task/companies`, {
          ...config,
        }),
      /**
       * @param {string} name company name
       * @param {boolean} leads It should include company leads in the searching?!
       */
      getAutoTaskCompanyByName: (name, leads, config) => {
        // FIXME: As describled on NWA-1021, we should be able to remove this approach soon, keeping `&leads=leads` only.
        const leadsQuery = leads ? `&leads=${leads}` : ``;
        return url.get(
          `/new/${API_VERSION}/integrations/auto_task/companies?name=${name}${leadsQuery}`,
          {
            ...config,
          }
        );
      },
      contactsByUri: retrieveContactsUri => url4.get(retrieveContactsUri),
      getCustomUrl: number =>
        url.get(
          `/new/${API_VERSION}/integrations/custom-urls/get-urls/${number}`
        ),
      goHighLevelLastMessages: (accountId, contactId) =>
        url.get(
          `/new/v1/integrations/gohighlevel/${accountId}/contacts/${contactId}/last-messages`
        ),
      goHighLevelGetMappedNumbers: accountId =>
        url.get(`/new/v1/integrations/gohighlevel/${accountId}/mapped-numbers`),
    },
    post: {
      createContact: (endpoint, data) => url4.post(endpoint, data),
      createTicket: (
        endpoint,
        externalId,
        callId,
        title,
        description,
        status,
        priority,
        queue
      ) =>
        url.post(`/new/${API_VERSION}/integrations/${endpoint}`, {
          external_id: externalId,
          call_id: callId,
          title,
          description,
          status,
          priority,
          queue,
        }),
      byDynamicURI: (endpoint, data) =>
        url.post(`/new/${API_VERSION}/integrations/${endpoint}`, data),
      /**
       * Contacts which will receive the call activity on Salesforce.
       * https://nuacom.atlassian.net/wiki/spaces/WA/pages/1980792877/Salesforce+Integration
       */
      syncCallWithCRMContacts: data =>
        url.post(
          `/new/${API_VERSION}/integrations/bulk-link-call-contact`,
          data
        ),
      unSyncCallForCRMContacts: data =>
        url.post(
          `/new/${API_VERSION}/integrations/bulk-unlink-call-contact`,
          data
        ),
    },
    put: {
      starConversation: (accountId, conversationId) =>
        url.put(
          `/new/v1/integrations/gohighlevel/${accountId}/conversations/${conversationId}/star`
        ),
      unmarkStarConversation: (accountId, conversationId) =>
        url.put(
          `/new/v1/integrations/gohighlevel/${accountId}/conversations/${conversationId}/unstar`
        ),
    },
  },
  afterCallWork: {
    get: {
      rules: () => url.get(`/new/${API_VERSION}/after-call-work`),
    },
  },
  conversation: {
    get: {
      getConversations: options =>
        axiosBaseUrlNew.get(`/new/v1/conversations`, options),
      getConversationById: id =>
        axiosBaseUrlNew.get(`/new/v1/conversations/${id}`),
      getUnreadMessagesCount: () =>
        axiosBaseUrlNew.get(`/new/v1/conversations/unread-count`),
    },
    post: {
      createConversation: conversation =>
        axiosBaseUrlNew.post(`/new/v1/conversations`, conversation),
      sendMessage: message =>
        axiosBaseUrlNew.post(`/new/v1/conversations/messages`, message),
      addMessageToConversation: (conversationId, message) =>
        axiosBaseUrlNew.post(
          `/new/v1/conversations/${conversationId}/messages`,
          message
        ),
    },
    patch: {
      archive: conversationId =>
        axiosBaseUrlNew.patch(
          `/new/v1/conversations/${conversationId}/archive`
        ),
      pin: conversationId =>
        axiosBaseUrlNew.patch(`/new/v1/conversations/${conversationId}/pin`),
      assign: (conversationId, data) =>
        axiosBaseUrlNew.patch(
          `/new/v1/conversations/${conversationId}/assign`,
          data
        ),
      readStatus: (conversationId, data) =>
        axiosBaseUrlNew.patch(
          `/new/v1/conversations/${conversationId}/read-status`,
          data
        ),
    },
    delete: {
      cancelMessage: (conversationId, messageId) =>
        axiosBaseUrlNew.delete(
          `/new/v1/conversations/${conversationId}/messages/${messageId}`
        ),
    },
  },
};

export default api;
